import React from 'react';

/**
 * The site's icon set. Every glyph is a 16-unit box, 1.5 stroke, round caps,
 * `currentColor` — so an icon always matches the type beside it, in either
 * theme, at any size. Nothing here is an emoji or a unicode stand-in.
 */

type P = { size?: number; strokeWidth?: number; style?: React.CSSProperties };

const Svg: React.FC<P & { children: React.ReactNode; fill?: boolean }> = ({
    size = 14, strokeWidth = 1.5, style, children, fill = false,
}) => (
    <svg
        width={size} height={size} viewBox="0 0 16 16"
        fill="none" stroke={fill ? 'none' : 'currentColor'}
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true" focusable="false"
        style={{ flexShrink: 0, display: 'block', ...style }}
    >
        {children}
    </svg>
);

export const ArrowRight: React.FC<P> = p => (
    <Svg {...p}><path d="M2.5 8h11M9.5 4l4 4-4 4" /></Svg>
);

export const ArrowLeft: React.FC<P> = p => (
    <Svg {...p}><path d="M13.5 8h-11M6.5 12l-4-4 4-4" /></Svg>
);

export const Play: React.FC<P> = p => (
    <Svg {...p}><path d="M4.5 2.75l8 5.25-8 5.25V2.75z" /></Svg>
);

export const Check: React.FC<P> = p => (
    <Svg {...p}><path d="M2.5 8.5l3.5 3.5 7.5-8" /></Svg>
);

export const Close: React.FC<P> = p => (
    <Svg {...p}><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" /></Svg>
);

/** Concentric sweep — the scan action. */
export const Radar: React.FC<P> = p => (
    <Svg {...p}>
        <circle cx="8" cy="8" r="6" />
        <circle cx="8" cy="8" r="2.75" />
        <path d="M8 8l4.25-4.25" />
    </Svg>
);

/** A phase that has not started. */
export const Pending: React.FC<P> = p => (
    <Svg {...p}><circle cx="8" cy="8" r="2.25" /></Svg>
);

/** A phase in flight — the arc spins. */
export const Running: React.FC<P> = ({ size = 14, strokeWidth = 1.75, style }) => (
    <svg
        width={size} height={size} viewBox="0 0 16 16"
        fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round"
        aria-hidden="true" focusable="false"
        style={{ flexShrink: 0, display: 'block', animation: 'rdr-spin 900ms linear infinite', ...style }}
    >
        <circle cx="8" cy="8" r="6" opacity="0.22" />
        <path d="M14 8a6 6 0 0 0-6-6" />
    </svg>
);

export const Alert: React.FC<P> = p => (
    <Svg {...p}><path d="M8 4.5v4.25M8 11.5v.01" /><circle cx="8" cy="8" r="6" /></Svg>
);

export const Lock: React.FC<P> = p => (
    <Svg {...p}>
        <rect x="3" y="7" width="10" height="6.5" />
        <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </Svg>
);

export const Database: React.FC<P> = p => (
    <Svg {...p}>
        <ellipse cx="8" cy="4" rx="5.25" ry="2.25" />
        <path d="M2.75 4v8c0 1.24 2.35 2.25 5.25 2.25s5.25-1.01 5.25-2.25V4" />
        <path d="M2.75 8c0 1.24 2.35 2.25 5.25 2.25S13.25 9.24 13.25 8" />
    </Svg>
);

/** Story traction, next to a points count. */
export const Upvote: React.FC<P> = p => (
    <Svg {...p}><path d="M8 3.25L13.25 11H2.75L8 3.25z" /></Svg>
);

export const Clock: React.FC<P> = p => (
    <Svg {...p}><circle cx="8" cy="8" r="6" /><path d="M8 4.75V8l2.25 1.5" /></Svg>
);

export const Keyframes = () => (
    <style>{`@keyframes rdr-spin{to{transform:rotate(360deg)}}`}</style>
);

/** Marks the lead story. Replaces the ✦ that used to stand in for an icon. */
export const Pin: React.FC<P> = p => (
    <Svg {...p}>
        <path d="M6 1.9h4l-.6 4.2 2.6 2.4H4l2.6-2.4L6 1.9z" />
        <path d="M8 8.5v5.6" />
    </Svg>
);

export const Calendar: React.FC<P> = p => (
    <Svg {...p}>
        <rect x="2.2" y="3.4" width="11.6" height="10.4" />
        <path d="M2.2 6.6h11.6M5.4 1.9v2.6M10.6 1.9v2.6" />
    </Svg>
);

/** Opens somewhere off this site. */
export const External: React.FC<P> = p => (
    <Svg {...p}>
        <path d="M9.4 2.4h4.2v4.2M13.6 2.4L7.4 8.6" />
        <path d="M12.2 9.6v3.2a.8.8 0 0 1-.8.8H3.2a.8.8 0 0 1-.8-.8V4.6a.8.8 0 0 1 .8-.8h3.2" />
    </Svg>
);

export const Flask: React.FC<P> = p => (
    <Svg {...p}>
        <path d="M6.4 1.9v4L2.9 12a1.2 1.2 0 0 0 1 1.9h8.2a1.2 1.2 0 0 0 1-1.9L9.6 5.9v-4" />
        <path d="M5.4 1.9h5.2M4.5 9.4h7" />
    </Svg>
);
